import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AmbulanceConflicts } from "@/components/AmbulanceConflicts";

const label = (v) => `${v.name}${v.plate ? ` (${v.plate})` : ""}`;

export function ChangeVehicleDialog({ svc, open, onOpenChange, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [oldId, setOldId] = useState("");
  const [newId, setNewId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
      setOldId(""); setNewId(""); setReason("");
    }
  }, [open]);

  const current = [...(svc.ambulances || []).map((a) => ({ ...a, amb: true })), ...(svc.required_vehicles || []).map((v) => ({ ...v, amb: false }))];
  const old = current.find((v) => v.id === oldId);
  const used = new Set(current.map((v) => v.id));
  const options = old ? vehicles.filter((v) => !used.has(v.id) && !!v.is_ambulance === old.amb) : [];
  const svcDates = svc.dates?.length ? svc.dates : [svc.date_time];

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/services/${svc.id}/change-vehicle`, { old_id: oldId, new_id: newId, reason });
      toast.success("Mezzo cambiato: gli iscritti sono stati avvisati");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-vehicle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Cambia mezzo</DialogTitle>
          <DialogDescription>Sostituisci un mezzo non più disponibile. Gli iscritti ricevono un avviso.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Mezzo da sostituire</Label>
            <Select value={oldId} onValueChange={(v) => { setOldId(v); setNewId(""); }}>
              <SelectTrigger data-testid="change-vehicle-old"><SelectValue placeholder="Scegli…" /></SelectTrigger>
              <SelectContent>{current.map((v) => <SelectItem key={v.id} value={v.id}>{v.amb ? "Ambulanza · " : ""}{label(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Nuovo mezzo</Label>
            <Select value={newId} onValueChange={setNewId} disabled={!old}>
              <SelectTrigger data-testid="change-vehicle-new"><SelectValue placeholder={old && !options.length ? (old.amb ? "Nessun'altra ambulanza disponibile" : "Nessun altro mezzo disponibile") : "Scegli…"} /></SelectTrigger>
              <SelectContent>{options.map((v) => <SelectItem key={v.id} value={v.id}>{label(v)}</SelectItem>)}</SelectContent>
            </Select>
            {old && !old.amb && <p className="text-[11px] text-muted-foreground">L'autista resta assegnato al nuovo mezzo (se il mezzo richiede la qualifica Autista e lui non l'ha, viene tolto e avvisato).</p>}
          </div>
          {old?.amb && newId && <AmbulanceConflicts ambIds={[newId]} dates={svcDates} ends={svcDates.map((d) => svc.end_times?.[d] || "")} excludeId={svc.id} />}
          <div className="space-y-1.5">
            <Label>Motivo <span className="text-muted-foreground font-normal">(facoltativo, incluso nell'avviso)</span></Label>
            <Textarea data-testid="change-vehicle-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. guasto, in officina…" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="change-vehicle-save" onClick={save} disabled={busy || !oldId || !newId} className="rounded-full font-semibold">
            {busy ? "Salvataggio…" : "Cambia mezzo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
